import React, { useMemo } from 'react';

import Box from '@amzn/meridian/box';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { MeasureUnitAbbreviations } from 'src/services/tasks/constants';

// **** Types **** //

interface HoursSelectorProps {
  selectedValue: string | undefined;
  onSelectValue: (value: string) => void;
  isDisabled: boolean;
  shiftLength: number;
  restrictionLabel: string;
  errorMessage: string | undefined;
  /** @testOnly */
  index: number;
}

interface DropdownOption {
  label: string;
  value: string;
}

// **** Components **** //

/**
 * Select hours value available based on the shift length.
 */
const HoursSelector = ({
  selectedValue,
  onSelectValue,
  isDisabled,
  shiftLength,
  restrictionLabel,
  errorMessage,
  index,
}: HoursSelectorProps) => {
  // This component does not make sense outside of the context of MeasureUnitValues.Hours
  // since we are quantizing all available selection values to be one hour increments
  // which may not be applicable to other measureUnit types. So "Hours" in hardcoded.
  const options: DropdownOption[] = useMemo(() => {
    return Array.from({ length: shiftLength }, (_, i) =>
      i === 0
        ? {
            label: `0 ${MeasureUnitAbbreviations.Hours} (Associate cannot ${restrictionLabel.toLowerCase()})`,
            value: '0',
          }
        : { label: `${i} ${MeasureUnitAbbreviations.Hours}`, value: String(i) },
    );
  }, [shiftLength, restrictionLabel]);

  return (
    <Box>
      <Text data-testid="new-jmr__time-restriction-field-label">
        Associate cannot perform: <b>{restrictionLabel}</b> exceeding (hrs) *
      </Text>
      <Select
        data-cy={`new-jmr__restrictions__hours-selector__dropdown__${index}`}
        placeholder="Select hours"
        value={selectedValue}
        onChange={onSelectValue}
        errorMessage={errorMessage}
        error={!!errorMessage}
        disabled={isDisabled}
      >
        {options.map((option, index) => (
          <SelectOption
            label={option.label}
            value={option.value}
            key={index}
            data-cy="new-jmr__hours-selector__dropdown-item"
            data-testid="new-jmr__hours-selector__dropdown-item"
          />
        ))}
      </Select>
    </Box>
  );
};

// **** Export **** //

export default HoursSelector;
